<template>
    <page :title="title" :breadcrumb="[{name: 'Listings', query: savedQueryParams}]">
        <list-loader v-if="!loaded"></list-loader>
        <div v-if="loaded && campsite !== null">
            <div class="listing-hero">
                <div class="listing-hero-inner">
                    <div class="container">
                        <div class="row">
                            <div class="col-md-7 col-lg-10 col-sm-12">
                                <div v-if="!campsite.mainimage" class="listing-hero-image hidden-sm-down"></div>
                                <div v-if="campsite.mainimage" class="listing-hero-image hidden-sm-down" :title="campsite.mainimage.name"
                                     :style="`background-image: url(${campsite.mainimage.source})`"></div>
                                <!-- /.listing-hero-image -->
                                <h1>{{ campsite.campsitename }} <i class="fa fa-check hidden-sm-down"></i></h1>
                                <address>
                                    {{ $t('detail_fullAddress', {number: campsite.number, street: campsite.straat, extension: campsite.extension || ''}) }}
                                    {{ campsite.city }}, {{ campsite.country }} {{ campsite.zipcode }}
                                </address>
                            </div>
                            <div class="col-md-5 col-lg-2 col-sm-12 mt-3 mt-md-0">
                                <button class="btn btn-primary btn-block" @click="reservation">
                                    <i class="fa fa-bookmark" aria-hidden="true"></i> {{ $t('detail_reservation') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- /.container -->
                </div>
                <!-- /.listing-hero-inner -->
            </div>
            <!-- /.listing-hero -->
            <div class="row">
                <div class="col-md-12 col-lg-12">
                    <div class="listing-detail-section" id="listing-detail-section-description" data-title="Description">

                        <div class="row my-3">
                            <div class="col-md-11">
                                <transition name="fade" mode="out-in">
                                    <img :src="images[selectedImage]" width="100%" :key="selectedImage" class="selected-image">
                                </transition>
                            </div>
                            <div class="col-md-1">
                                <img v-for="(image, imageIndex) in images"
                                     @click="selectedImage = imageIndex"
                                     :key="imageIndex"
                                     :src="image"
                                     width="100%"
                                     class="image-thumbnail"
                                     :class="{active: selectedImage === imageIndex}"
                                >
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-5">
                                <div class="overview">
                                    <h2>{{ $t('detail_ListingAttributes') }}</h2>
                                    <ul>
                                        <li><strong>{{ $t('detail_Pitches') }}</strong><span>{{ campsite.pitches }}</span></li>
                                        <li><strong>{{ $t('detail_Extension') }}</strong><span>{{ campsite.extension || '-' }}</span></li>
                                        <li><strong>{{ $t('detail_DogsAllowed') }}</strong><span>{{ campsite.dogsallowed ? 'Yes' : 'No' }}</span></li>
                                    </ul>
                                </div>
                                <!-- /.overview -->
                            </div>
                            <!-- /.col-* -->
                            <div class="col-lg-7">
                                <h2>{{ $t('detail_Description') }}</h2>
                                <p v-html="campsite.generalinformation"></p>
                            </div>
                            <!-- /.col-* -->
                        </div>
                        <!-- /.row -->
                    </div>
                    <!-- /.listing-detail-section -->
                    <div class="listing-detail-section" id="listing-detail-section-amenities" data-title="Amenities">
                        <h2>{{ $t('detail_Characteristics') }}</h2>
                        <div class="row mb-3">
                            <div class="col-md-3 mb-3" v-for="(char, index) in campsite.characteristics" :key="index">
                                <img class="icon-img" :src="baseURL + char.source"/>
                                <span class="icon-text">{{ char.hint}}</span>
                            </div>
                        </div>
                    </div>
                    <!-- /.listing-detail-section -->
                    <div class="listing-detail-section" id="listing-detail-section-contact-information" data-title="Contact">
                        <h2>{{ $t('detail_ContactInformation') }}</h2>
                        <div class="box">
                            <div class="box-inner">
                                <div class="overview overview-half overview-no-margin">
                                    <ul>
                                        <li><strong>{{ $t('detail_Country') }}</strong><span>{{ campsite.country }}</span></li>
                                        <li><strong>{{ $t('detail_City') }}</strong><span>{{ campsite.city }}</span></li>
                                        <li><strong>{{ $t('detail_Address') }}</strong><span>{{ $t('detail_fullAddress', {number: campsite.number, street: campsite.straat, extension: campsite.extension || ''}) }}</span></li>
                                        <li><strong>{{ $t('detail_zipCode') }}</strong><span>{{ campsite.zipcode }}</span></li>
                                        <li><strong>{{ $t('detail_Email') }}</strong><span><a :href="`mailto:${campsite.email}`">{{ campsite.email || '-' }}</a></span></li>
                                        <li><strong>{{ $t('detail_Phone') }}</strong><span>{{ campsite.telephone || '-' }}</span></li>
                                        <li><strong>{{ $t('detail_Website') }}</strong><span><a :href="campsite.website">{{ campsite.website || '-' }}</a></span></li>
                                    </ul>
                                </div>
                                <!-- /.overview -->
                            </div>
                            <!-- /.box-inner -->
                        </div>
                        <!-- /.box -->
                    </div>
                    <!-- /.listing-detail-section -->
                    <div class="listing-detail-section" id="listing-detail-section-map-position" data-title="Map Position">
                        <h2>{{ $t('detail_MapPosition') }}</h2>
                        <gmap-map
                                :center="center"
                                :zoom="12"
                                style="width:100%;  height: 300px; margin-bottom: 20px;"
                        >
                            <gmap-marker
                                    :position="center"
                            ></gmap-marker>
                        </gmap-map>
                    </div>
                </div>
                <!-- /.col-* -->
            </div>
            <!-- /.row -->
        </div>
    </page>
</template>

<script>
import Page from './utilities/Page'
import { mapGetters } from 'vuex'
import { API } from '../api/axios'
import { ListLoader } from 'vue-content-loader'

export default {
  name: 'ListingDetail',
  components: {Page, ListLoader},
  data () {
    return {
      campsite: null,
      selectedImage: 0,
      loaded: false,
    }
  },
  computed: {
    ...mapGetters(['language', 'savedQueryParams']),
    images () {
      if (this.campsite.imagelist === undefined) {
        return []
      } else {
        return this.campsite.imagelist.map(image => image.source)
      }
    },
    center () {
      return {
        lat: this.campsite.latitude,
        lng: this.campsite.longitude,
      }
    },
    title () {
      if (!this.campsite) {
        return 'Campsite detail'
      }

      return this.campsite.campsitename
    },
    baseURL: () => window.configECP.baseURL,
  },
  methods: {
    reservation () {
      this.$router.push({name: 'Reservation', params: {campsite: this.$route.params.id}})
    },
  },
  mounted () {
    let campsiteId = this.$route.params.id
    API.get(`/campsites/${campsiteId}?languageId=${this.language}`)
      .then(response => {
        this.campsite = response.data.campsitelist[0]
        this.loaded = true
      })
  },
}
</script>

<style scoped lang="scss">
  /*@import '../scss/helpers/variables';*/

</style>



// WEBPACK FOOTER //
// src/components/ListingDetail.vue